import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateUserPublicProfile } from './api';
import type { UserDetail } from './types';

interface Props {
  user: UserDetail;
}

interface FormState {
  publicSlug: string;
  publicTitle: string;
  publicBio: string;
  publicAvatarUrl: string;
  credentialsInput: string;
  linkedin: string;
  x: string;
  github: string;
  website: string;
}

function userToForm(u: UserDetail): FormState {
  const links = (u.socialLinks ?? {}) as Record<string, string>;
  return {
    publicSlug: u.publicSlug ?? '',
    publicTitle: u.publicTitle ?? '',
    publicBio: u.publicBio ?? '',
    publicAvatarUrl: u.publicAvatarUrl ?? '',
    credentialsInput: (u.credentials ?? []).join('\n'),
    linkedin: links.linkedin ?? '',
    x: links.x ?? '',
    github: links.github ?? '',
    website: links.website ?? '',
  };
}

export function PublicProfileForm({ user }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(userToForm(user));
  const [message, setMessage] = useState<{ text: string; variant: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setForm(userToForm(user));
  }, [user.id]);

  const mutation = useMutation({
    mutationFn: () => {
      const socialLinks: Record<string, string> = {};
      if (form.linkedin.trim()) socialLinks.linkedin = form.linkedin.trim();
      if (form.x.trim()) socialLinks.x = form.x.trim();
      if (form.github.trim()) socialLinks.github = form.github.trim();
      if (form.website.trim()) socialLinks.website = form.website.trim();

      return updateUserPublicProfile(user.id, {
        publicSlug: form.publicSlug.trim() || null,
        publicTitle: form.publicTitle.trim() || null,
        publicBio: form.publicBio.trim() || null,
        publicAvatarUrl: form.publicAvatarUrl.trim() || null,
        credentials: form.credentialsInput
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        socialLinks: Object.keys(socialLinks).length > 0 ? socialLinks : null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', user.id] });
      setMessage({ text: 'Public-Profil gespeichert.', variant: 'success' });
    },
    onError: (err: Error) => setMessage({ text: err.message, variant: 'error' }),
  });

  const isEditor = user.role === 'ADMIN' || user.role === 'REVIEWER';

  return (
    <div className="rounded-xl bg-bg-surface border border-border-subtle p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold mb-1">Public-Profil (Article-CMS)</h3>
          <p className="text-sm text-text-secondary">
            Wenn <code className="px-1.5 py-0.5 rounded bg-bg-elevated text-text-tertiary text-xs">publicSlug</code> gesetzt ist, wird der User unter{' '}
            <code className="text-accent text-xs">/team/&lt;slug&gt;</code> öffentlich verlinkt — als Article-Author oder Reviewer.
          </p>
        </div>
        {!isEditor && (
          <span className="text-xs text-warning bg-warning/10 px-2 py-1 rounded-md">
            Sichtbar nur mit Role ADMIN oder REVIEWER
          </span>
        )}
      </div>

      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
              Public-Slug
            </label>
            <input
              value={form.publicSlug}
              onChange={(e) => setForm({ ...form, publicSlug: e.target.value })}
              placeholder="z.B. fabian-golz"
              className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default text-sm font-mono"
            />
            <p className="text-[11px] text-text-tertiary mt-1">URL: /team/{form.publicSlug || '<slug>'}</p>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
              Public-Title
            </label>
            <input
              value={form.publicTitle}
              onChange={(e) => setForm({ ...form, publicTitle: e.target.value })}
              placeholder="z.B. Founder · Heilpraktiker-Anwärter"
              className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
            Public-Bio
          </label>
          <textarea
            value={form.publicBio}
            onChange={(e) => setForm({ ...form, publicBio: e.target.value })}
            placeholder="2-4 Sätze über die Person — wird auf /team/<slug> in Section &quot;Über mich&quot; gerendert."
            rows={4}
            className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default text-sm"
          />
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
            Avatar-URL
          </label>
          <input
            value={form.publicAvatarUrl}
            onChange={(e) => setForm({ ...form, publicAvatarUrl: e.target.value })}
            placeholder="https://… (echtes Foto, square, mind. 256×256)"
            className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default text-sm font-mono"
          />
          <p className="text-[11px] text-text-tertiary mt-1">
            Wenn leer: Initialen-Avatar wird gerendert. E-E-A-T-Hinweis: echtes Foto ist ein starkes Trust-Signal.
          </p>
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
            Credentials (eine pro Zeile)
          </label>
          <textarea
            value={form.credentialsInput}
            onChange={(e) => setForm({ ...form, credentialsInput: e.target.value })}
            placeholder={`Software-Engineer, 12+ Jahre\nHeilpraktiker-Anwärter`}
            rows={3}
            className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default text-sm"
          />
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
            Social-Links
          </label>
          <div className="grid sm:grid-cols-2 gap-2">
            <input
              value={form.linkedin}
              onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
              placeholder="LinkedIn URL"
              className="px-3 py-2 rounded-lg bg-bg-elevated border border-border-default text-sm font-mono"
            />
            <input
              value={form.x}
              onChange={(e) => setForm({ ...form, x: e.target.value })}
              placeholder="X (Twitter) URL"
              className="px-3 py-2 rounded-lg bg-bg-elevated border border-border-default text-sm font-mono"
            />
            <input
              value={form.github}
              onChange={(e) => setForm({ ...form, github: e.target.value })}
              placeholder="GitHub URL"
              className="px-3 py-2 rounded-lg bg-bg-elevated border border-border-default text-sm font-mono"
            />
            <input
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="Website URL"
              className="px-3 py-2 rounded-lg bg-bg-elevated border border-border-default text-sm font-mono"
            />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="px-4 py-2 rounded-lg font-semibold text-white bg-accent hover:bg-accent-hover disabled:opacity-50"
          >
            Public-Profil speichern
          </button>
          {form.publicSlug && (
            <a
              href={`https://currivium.de/team/${form.publicSlug}`}
              target="_blank"
              rel="noopener"
              className="text-sm text-accent hover:underline"
            >
              Live ansehen ↗
            </a>
          )}
        </div>

        {message && (
          <div
            className={`rounded-lg px-4 py-3 text-sm ${
              message.variant === 'success' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
            }`}
          >
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
}
